/**
 * Página de administración de productos
 * 
 * Muestra un listado de productos y permite realizar operaciones CRUD.
 * Diseñado para ser compatible con migración futura a Firebase.
 * 
 * @module pages/productos/Productos
 * @requires react, react-router-dom, react-toastify
 * @related_files ../../services/productos.service.js, ../../services/categorias.service.js
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  FaEye,
  FaBox,
  FaFilter,
  FaTags,
  FaTimes,
  FaFileExport,
  FaMagic,
  FaDollarSign,
  FaBolt,
  FaLink,
  FaFileImport,
  FaBalanceScale
} from 'react-icons/fa';


// Servicios
import productosService from '../../services/productos.service';
import { useAuth } from '../../contexts/AuthContext';
import categoriasService from '../../services/categorias.service';


// Componentes
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Table from '../../components/common/Table';
import SearchBar from '../../components/common/SearchBar';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ImportarExcel from '../../components/modules/productos/ImportarExcel';
import ProductLoadAssistant from '../../components/modules/productos/ProductLoadAssistant';


// Utilidades
import { formatCurrency } from '../../utils/format';
import { getProductPluBalanza } from '../../utils/balanza/pluBalanza';
import {
  isProductoPendienteDePrecio,
  debeAbrirActivacionMaster,
  isProductoMasterImportado,
  getProductoPendientePrecioLabel,
  getEstadoProductoMasterTheme
} from '../../utils/masterCatalogProduct';
import ActivarProductoMasterModal from '../../components/modules/productos/ActivarProductoMasterModal';
import EstadoProductoMasterBadge from '../../components/modules/productos/EstadoProductoMasterBadge';
import CatalogActivationMetrics from '../../components/modules/productos/CatalogActivationMetrics';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
  parseListasPreciosFromEmpresaConfig,
  resolveProductSalePrice
} from '../../utils/listasPreciosLabels';
import { FILTRO_PRODUCTOS_SIN_COSTO, productoSinCosto } from '../../utils/productoCosto';

/**
 * Componente de página de administración de productos
 * @returns {JSX.Element} Componente Productos
 */
const Productos = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sucursalSeleccionada, orgId } = useAuth();
  // Estados
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showLoadAssistant, setShowLoadAssistant] = useState(false);
  const [filtroPendientesPrecio, setFiltroPendientesPrecio] = useState(false);
  const [filtroSinCosto, setFiltroSinCosto] = useState(false);
  const [activacionMaster, setActivacionMaster] = useState({ open: false, producto: null });
  const [listaPrecioDisplay, setListaPrecioDisplay] = useState('mayorista');

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const filtro = String(params.get('filtro') || '').trim().toLowerCase();
    if (filtro === FILTRO_PRODUCTOS_SIN_COSTO || filtro === 'sin_costo' || filtro === 'sincosto') {
      setFiltroSinCosto(true);
      setFiltroPendientesPrecio(false);
    }
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;
    async function cargarListaEmpresa() {
      if (!orgId) return;
      try {
        const snap = await getDoc(doc(db, `companies/${orgId}/config/empresa`));
        if (cancelled) return;
        const parsed = parseListasPreciosFromEmpresaConfig(snap.exists() ? snap.data() : {});
        setListaPrecioDisplay(parsed.lista_precio_punto_venta_default);
      } catch {
        if (!cancelled) setListaPrecioDisplay('mayorista');
      }
    }
    cargarListaEmpresa();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const precioDisplayProducto = useCallback(
    (producto) => resolveProductSalePrice(producto, listaPrecioDisplay),
    [listaPrecioDisplay]
  );

  const productosVisibles = useMemo(() => {
    let list = productos;
    if (filtroSinCosto) {
      list = list.filter((p) => productoSinCosto(p));
    }
    if (filtroPendientesPrecio) {
      list = list.filter((p) => isProductoPendienteDePrecio(p));
    }
    return list;
  }, [productos, filtroPendientesPrecio, filtroSinCosto]);

  const pendientesPrecioCount = useMemo(
    () => productos.filter((p) => isProductoPendienteDePrecio(p)).length,
    [productos]
  );

  const sinCostoCount = useMemo(
    () => productos.filter((p) => productoSinCosto(p)).length,
    [productos]
  );

  // Estado para diálogo de confirmación de eliminación
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });
  
  /**
   * Carga inicial de datos
   */
	useEffect(() => {
	  // Verificar si viene de crear/editar con señal de recarga
	  const shouldReload = location.state?.reload;
	  
	  if (shouldReload) {
		// Limpiar el state para evitar recargas infinitas
		navigate('/productos', { replace: true, state: {} });
	  }
	  
	const fetchData = async () => {
	  try {
		setLoading(true);
		
		// Obtener productos
		console.log('Solicitando productos...');
		const response = await productosService.obtenerTodos();
		console.log('Respuesta de productos:', response);
		
		// Verificar la estructura de datos
		if (Array.isArray(response)) {
		  console.log(`Productos cargados: ${response.length}`);
		  setProductos(response);
		} else if (response && response.data && Array.isArray(response.data)) {
		  console.log(`Productos cargados: ${response.data.length}`);
		  setProductos(response.data);
		} else {
		  console.error('Formato de respuesta inesperado:', response);
		  setProductos([]);
		  toast.error('No se pudieron cargar los productos');
		}
		  
		} catch (error) {
		  console.error('Error al cargar productos:', error);
		  toast.error('Error al cargar datos');
		  setProductos([]);
		} finally {
		  setLoading(false);
		}
	  };
	  
	  fetchData();
	}, [location.state, navigate]);
	
	// Agregar un useEffect adicional para recargar cuando cambie la URL
	useEffect(() => {
	  const fetchData = async () => {
		try {
		  setLoading(true);
		  console.log('🔄 Recargando productos...');
		  const response = await productosService.obtenerTodos();
		  console.log('🔄 Respuesta de recarga:', response);
		  
		  if (Array.isArray(response)) {
			console.log(`🔄 Productos recargados: ${response.length}`);
			setProductos(response);
		  } else if (response && response.data && Array.isArray(response.data)) {
			console.log(`🔄 Productos recargados: ${response.data.length}`);
			setProductos(response.data);
		  }
		} catch (error) {
		  console.error('🔄 Error al recargar productos:', error);
		} finally {
		  setLoading(false);
		}
	  };
	  
	  // Recargar cuando se monte el componente
	  fetchData();
	}, []);

  // Recargar productos cuando cambia la sucursal seleccionada
  useEffect(() => {
    const cargarPorSucursal = async () => {
      try {
        if (!sucursalSeleccionada?.id) return;
        setLoading(true);
        console.log('📍 [PRODUCTOS] Sucursal cambiada, recargando productos con stock por sucursal:', sucursalSeleccionada.id);
        const lista = await productosService.buscarConStockPorSucursal('', sucursalSeleccionada.id);
        setProductos(Array.isArray(lista) ? lista : []);
      } catch (e) {
        console.error('❌ [PRODUCTOS] Error cargando productos por sucursal:', e);
      } finally {
        setLoading(false);
      }
    };
    cargarPorSucursal();
  }, [sucursalSeleccionada?.id]);
	  
  /**
   * Búsqueda de productos
   */
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      // Si la búsqueda está vacía, cargar todos los productos
      try {
        setLoading(true);
        const data = await productosService.obtenerTodos();
        setProductos(data);
      } catch (error) {
        console.error('Error al cargar productos:', error);
        toast.error('Error en la búsqueda');
      } finally {
        setLoading(false);
      }
      return;
    }
    
    try {
      setLoading(true);
      console.log(`🔍 Buscando productos: "${searchTerm}"`);
      const productosEncontrados = await productosService.buscar(searchTerm);
      
      // Verificar si el servicio devolvió productos sin filtrar
      if (productosEncontrados && productosEncontrados.length > 0) {
        console.log(`⚠️ Servicio devolvió ${productosEncontrados.length} productos, verificando filtrado...`);
        
        // Aplicar filtro local si es necesario
        const terminoLower = searchTerm.toLowerCase();
        const productosFiltrados = productosEncontrados.filter(p => {
          const coincideNombre = p.nombre?.toLowerCase().includes(terminoLower);
          const coincideCodigo = p.codigo?.toLowerCase().includes(terminoLower);
          const coincideDescripcion = p.descripcion?.toLowerCase().includes(terminoLower);
          
          return coincideNombre || coincideCodigo || coincideDescripcion;
        });
        
        console.log(`🔍 Productos filtrados localmente: ${productosFiltrados.length} de ${productosEncontrados.length}`);
        setProductos(productosFiltrados);
      } else {
        setProductos([]);
      }
    } catch (error) {
      console.error('Error en búsqueda:', error);
      toast.error('Error en la búsqueda');
      setProductos([]);
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Limpia la búsqueda y filtros
   */
  const handleClearSearch = async () => {
    setSearchTerm('');
    setCategoriaSeleccionada('');
    setFiltroSinCosto(false);
    if (location.search) {
      navigate('/productos', { replace: true });
    }
    
    try {
      setLoading(true);
      const data = await productosService.obtenerTodos();
      setProductos(data);
    } catch (error) {
      console.error('Error al restablecer productos:', error);
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const quitarFiltroSinCosto = () => {
    setFiltroSinCosto(false);
    if (location.search.includes('filtro=')) {
      navigate('/productos', { replace: true });
    }
  };
  
  /**
   * Filtra productos por categoría
   */
  const handleFilterByCategory = async (e) => {
    const categoriaId = e.target.value;
    setCategoriaSeleccionada(categoriaId);
    
    try {
      setLoading(true);
      
      if (!categoriaId) {
        // Si no hay categoría seleccionada, mostrar todos
        const data = await productosService.obtenerTodos();
        setProductos(data);
      } else {
        // Filtrar por categoría
        const data = await productosService.obtenerPorCategoria(categoriaId);
        setProductos(data);
      }
    } catch (error) {
      console.error('Error al filtrar por categoría:', error);
      toast.error('Error al filtrar productos');
      
      // Si falla, realizar filtrado local como respaldo
      try {
        const allProducts = await productosService.obtenerTodos();
        if (!categoriaId) {
          setProductos(allProducts);
        } else {
          const filtered = allProducts.filter(p => 
            p.categoria_id === parseInt(categoriaId) || p.categoria === categoriaId
          );
          setProductos(filtered);
        }
      } catch (fallbackError) {
        console.error('Error en filtrado local:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Abre el diálogo de confirmación para eliminar
   * @param {Object} producto - Producto a eliminar
   */
  const handleDeleteClick = (producto) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Confirmar eliminación',
      message: `¿Estás seguro de eliminar el producto "${producto.nombre}"?`,
      onConfirm: () => handleDelete(producto.id)
    });
  };
 
 //Maneja el éxito de la importación
 
	const handleImportSuccess = () => {
	  // Recargar productos después de importar
	  const fetchData = async () => {
		try {
		  setLoading(true);
		  const response = await productosService.obtenerTodos();
		  
		  if (Array.isArray(response)) {
			setProductos(response);
		  } else if (response && response.data && Array.isArray(response.data)) {
			setProductos(response.data);
		  }
		} catch (error) {
		  console.error('Error al recargar productos:', error);
		  toast.error('Error al recargar productos');
		} finally {
		  setLoading(false);
		}
	  };
	  
	  fetchData();
	  toast.success('Productos importados correctamente');
	};
  /**
   * Elimina un producto
   * @param {number} id - ID del producto
   */
  const handleDelete = async (id) => {
    try {
      await productosService.eliminar(id);
      
      // Actualizar lista de productos
      setProductos(productos.filter(producto => producto.id !== id));
      
      toast.success('Producto eliminado correctamente');
    } catch (error) {
      console.error('Error al eliminar producto:', error);
      toast.error('Error al eliminar el producto');
    } finally {
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };
  
  /**
   * Redirecciona a la página de detalle de producto
   * @param {number} id - ID del producto
   */
  const handleViewClick = (id) => {
    navigate(`/productos/${id}`);
  };
  
  /**
   * Redirecciona a la página de edición de producto
   * @param {number} id - ID del producto
   */
  const handleCargarPrecioClick = (row) => {
    if (debeAbrirActivacionMaster(row)) {
      setActivacionMaster({ open: true, producto: row });
      return;
    }
    navigate(`/productos/editar/${row.id}`, { state: { focusPrecio: true } });
  };

  const handleActivacionMasterSuccess = (updated) => {
    setProductos((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    setActivacionMaster({ open: false, producto: null });
  };

  const handleEditClick = (id) => {
    navigate(`/productos/editar/${id}`);
  };
  
  /**
   * Exporta productos a CSV
   */
  const handleExport = () => {
    try {
      // Convertir productos a CSV
      const headers = ['ID', 'Código', 'Nombre', 'Precio', 'Categoría', 'Stock'];
      const data = productos.map(p => [
        p.id,
        p.codigo,
        p.nombre,
        p.precio_venta,
        p.categoria,
        p.stock_actual
      ]);
      
      const csvContent = [
        headers.join(','),
        ...data.map(row => row.join(','))
      ].join('\n');
      
      // Crear Blob y descargar
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'productos.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Productos exportados exitosamente');
    } catch (error) {
      console.error('Error al exportar productos:', error);
      toast.error('Error al exportar productos');
    }
  };
  
  /**
 * Columnas para la tabla de productos - CORREGIDAS PARA FIREBASE
 */
	const columns = [
	  {
		header: 'Código',
		accessor: 'codigo',
		cell: (row) => {
		  const plu = getProductPluBalanza(row);
		  return (
		    <div>
		      <span className="font-medium">{row.codigo || 'N/A'}</span>
		      {plu && (
		        <div className="text-xs text-e0e3-accent font-medium mt-0.5">PLU: {plu}</div>
		      )}
		    </div>
		  );
		}
	  },
	  {
		header: 'Nombre',
		accessor: 'nombre',
		cell: (row) => (
		  <div>
			<div className="font-medium flex flex-wrap items-center gap-2">
			  <span>{row.nombre || 'Sin nombre'}</span>
			  {isProductoMasterImportado(row) ? (
			    <EstadoProductoMasterBadge
			      producto={row}
			      stockSucursal={row.stock_actual ?? row.stock?.cantidad ?? row.stock_sucursal}
			    />
			  ) : isProductoPendienteDePrecio(row) ? (
			    <span
			      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${getEstadoProductoMasterTheme('pendiente_precio').badgeClass}`}
			      title={getEstadoProductoMasterTheme('pendiente_precio').tooltip}
			    >
			      {getProductoPendientePrecioLabel()}
			    </span>
			  ) : null}
			</div>
			<div className="text-sm text-e0e3-muted">
			  {row.categoria?.nombre || row.categoria || 'Sin categoría'}
			</div>
		  </div>
		)
	  },
	  {
		header: 'Precio',
		accessor: 'precio_venta',
		cell: (row) => (
		  <span className={`font-medium ${isProductoPendienteDePrecio(row) ? 'text-amber-700' : ''}`}>
			{isProductoPendienteDePrecio(row)
        ? 'Sin precio'
        : formatCurrency(precioDisplayProducto(row))}
		  </span>
		)
	  },
	  {
		header: 'Stock',
		accessor: 'stock',
		cell: (row) => {
		  const stockActual = parseFloat(row.stock_actual ?? row.stock_sucursal ?? row.stock?.cantidad ?? 0) || 0;
		  const stockMinimo = row.stock?.stock_minimo || row.stock_minimo || 5;
		  
		  let stockClass = 'text-emerald-300 bg-emerald-500/15 border border-emerald-500/30';

		  if (stockActual < 0) {
			stockClass = 'text-red-300 bg-red-500/20 border border-red-500/40';
		  } else if (stockActual <= 0) {
			stockClass = 'text-red-300 bg-red-500/15 border border-red-500/30';
		  } else if (stockActual <= stockMinimo) {
			stockClass = 'text-amber-300 bg-amber-500/15 border border-amber-500/30';
		  } else if (stockActual <= stockMinimo * 1.5) {
			stockClass = 'text-yellow-300 bg-yellow-500/15 border border-yellow-500/30';
		  }
		  
		  return (
			<span className={`px-2 py-1 rounded-full text-sm font-semibold ${stockClass}`}>
			  {stockActual}
			</span>
		  );
		}
	  },
	  {
		header: 'Acciones',
		accessor: 'acciones',
		cell: (row) => (
		  <div className="flex space-x-2">
			{debeAbrirActivacionMaster(row) && (
			  <button
			    onClick={() => handleCargarPrecioClick(row)}
			    className="text-amber-700 hover:text-amber-900"
			    title="Activar producto"
			  >
			    <FaDollarSign />
			  </button>
			)}
			<button
			  onClick={() => handleViewClick(row.id)}
			  className="text-blue-600 hover:text-e0e3-accent-soft"
			  title="Ver detalles"
			>
			  <FaEye />
			</button>
			
			<button
			  onClick={() => handleEditClick(row.id)}
			  className="text-e0e3-ok hover:text-green-800"
			  title="Editar"
			>
			  <FaEdit />
			</button>
			
			<button
			  onClick={() => handleDeleteClick(row)}
			  className="text-e0e3-danger hover:text-red-800"
			  title="Eliminar"
			>
			  <FaTrash />
			</button>
		  </div>
		)
	  }
	];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-e0e3-foreground">Productos</h1>
          <p className="mt-1 text-sm text-e0e3-muted">Stock, precios e importación desde un solo lugar.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Link to="/productos/precios">
            <Button color="info" icon={<FaTags />}>
              Gestión de Precios
            </Button>
          </Link>
          <Link to="/productos/equivalencias">
            <Button color="secondary" icon={<FaLink />}>
              Equivalencias
            </Button>
          </Link>
          <Link to="/productos/listas-proveedor">
            <Button color="secondary" icon={<FaFileImport />}>
              Lista de proveedor
            </Button>
          </Link>
          <Link to="/productos/comparador-precios">
            <Button color="secondary" icon={<FaBalanceScale />}>
              Comparar precios
            </Button>
          </Link>
          <Button color="secondary" icon={<FaFileExport />} onClick={handleExport}>
            Exportar
          </Button>
        </div>
      </div>

      <CatalogActivationMetrics productos={productos} />

      <div className="overflow-hidden rounded-2xl bg-gradient-to-r from-violet-600 via-indigo-600 to-sky-600 p-[1px] shadow-lg shadow-indigo-500/15">
        <div className="nexo-surface flex flex-col gap-4 rounded-2xl p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md">
              <FaMagic className="text-lg" aria-hidden />
            </div>
            <div>
              <p className="font-semibold text-e0e3-foreground">¿Todavía no cargaste todo el catálogo?</p>
              <p className="text-sm text-e0e3-muted">
                Usá el asistente: paso a paso, Excel o formulario clásico.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              color="primary"
              icon={<FaMagic />}
              onClick={() => setShowLoadAssistant(true)}
            >
              Asistente para cargar productos
            </Button>
            <Link to="/productos/nuevo">
              <Button color="secondary" icon={<FaPlus />}>
                Nuevo (formulario)
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <Card>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-grow">
            <SearchBar
              placeholder="Buscar productos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={handleSearch}
              onClear={handleClearSearch}
            />
          </div>
          
          <div className="md:w-64">
            <select
              value={categoriaSeleccionada}
              onChange={handleFilterByCategory}
              className="nexo-field"
            >
              <option value="">Todas las categorías</option>
              {categorias.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {filtroSinCosto && (
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/15 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-amber-100">
                Productos sin costo registrado
                <span className="tabular-nums opacity-80">({sinCostoCount})</span>
                <button
                  type="button"
                  onClick={quitarFiltroSinCosto}
                  className="rounded-full p-0.5 hover:bg-amber-500/30"
                  title="Quitar filtro"
                  aria-label="Quitar filtro Productos sin costo registrado"
                >
                  <FaTimes size={10} />
                </button>
              </span>
            )}
            <Button
              color={filtroSinCosto ? 'warning' : 'secondary'}
              icon={<FaDollarSign />}
              onClick={() => {
                if (filtroSinCosto) {
                  quitarFiltroSinCosto();
                } else {
                  setFiltroSinCosto(true);
                  setFiltroPendientesPrecio(false);
                  navigate('/productos?filtro=sin-costo', { replace: true });
                }
              }}
            >
              {filtroSinCosto ? 'Ver catálogo completo' : `Sin costo (${sinCostoCount})`}
            </Button>
            {filtroSinCosto && (
              <p className="w-full text-xs text-e0e3-muted leading-snug">
                Catálogo actual sin costo registrado. No lista ventas históricas con costo no determinable.
              </p>
            )}
            <Button
              color={filtroPendientesPrecio ? 'warning' : 'secondary'}
              icon={<FaDollarSign />}
              onClick={() => {
                setFiltroPendientesPrecio((prev) => {
                  const next = !prev;
                  if (next) setFiltroSinCosto(false);
                  return next;
                });
              }}
            >
              {filtroPendientesPrecio ? 'Ver todos' : `Pendientes (${pendientesPrecioCount})`}
            </Button>
            {pendientesPrecioCount > 0 && (
              <Link to="/productos/activacion-masiva">
                <Button color="primary" icon={<FaBolt />}>
                  Carga masiva
                </Button>
              </Link>
            )}
          </div>
        </div>
      </Card>
      
      <Card>
        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            {(() => {
              console.log('🔍 [RENDER] Estado de productos:', {
                productosLength: productos.length,
                productos: productos,
                loading: loading
              });
              
              return productosVisibles.length === 0 ? (
                <div className="bg-white/[0.04] p-8 rounded-lg text-center">
                  <FaBox className="mx-auto text-4xl text-e0e3-muted mb-2" />
                  <h3 className="text-lg font-medium text-e0e3-foreground mb-1">
                    No hay productos disponibles
                  </h3>
                  <p className="text-e0e3-muted">
                    {filtroSinCosto
                      ? 'No hay productos sin costo con los filtros actuales'
                      : filtroPendientesPrecio
                      ? 'No hay productos pendientes de precio con los filtros actuales'
                      : searchTerm || categoriaSeleccionada
                      ? 'No se encontraron resultados para tu búsqueda'
                      : 'Comienza agregando productos a tu inventario'
                    }
                  </p>
                  
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    <Button color="primary" icon={<FaMagic />} onClick={() => setShowLoadAssistant(true)}>
                      Asistente para cargar
                    </Button>
                    <Link to="/productos/nuevo">
                      <Button color="secondary" icon={<FaPlus />}>
                        Nuevo producto
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <Table
                  columns={columns}
                  data={productosVisibles}
                  pagination={true}
                  itemsPerPage={10}
                />
              );
            })()}
          </>
        )}
      </Card>
      
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />
      <ProductLoadAssistant
        isOpen={showLoadAssistant}
        onClose={() => setShowLoadAssistant(false)}
        onOpenImport={() => setShowImportDialog(true)}
      />
      <ImportarExcel
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImportSuccess={handleImportSuccess}
      />
      <ActivarProductoMasterModal
        isOpen={activacionMaster.open}
        producto={activacionMaster.producto}
        sucursalId={sucursalSeleccionada?.id}
        orgId={orgId}
        onClose={() => setActivacionMaster({ open: false, producto: null })}
        onSuccess={handleActivacionMasterSuccess}
      />
    </div>
  );
};

export default Productos;